import React, { useState, useEffect } from 'react';
import { creatorsAPI } from '../api/creators';

const EditCreatorModal = ({ isOpen, onClose, onSuccess, creator }) => {
  const [formData, setFormData] = useState({
    name: '',
    handle: '',
    twitchUrl: '',
    kickUrl: '',
    youtubeUrl: '',
    tiktokUrl: '',
    twitterUrl: '',
    instagramUrl: '',
    discordUrl: '',
    contactEmail: '',
    country: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [refreshingIds, setRefreshingIds] = useState(false);
  const [findingEmail, setFindingEmail] = useState(false);
  const [updatingImage, setUpdatingImage] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Populate form when creator changes
  useEffect(() => {
    if (creator) {
      setFormData({
        name: creator.name || '',
        handle: creator.handle || '',
        twitchUrl: creator.twitchUrl || '',
        kickUrl: creator.kickUrl || '',
        youtubeUrl: creator.youtubeUrl || '',
        tiktokUrl: creator.tiktokUrl || '',
        twitterUrl: creator.twitterUrl || '',
        instagramUrl: creator.instagramUrl || '',
        discordUrl: creator.discordUrl || '',
        contactEmail: creator.contactEmail || creator.email || '',
        country: creator.country || '',
        notes: creator.notes || ''
      });
      // Only set profileImageUrl if it's a valid public URL (not a data URL)
      const url = creator.profileImageUrl || '';
      if (url && !url.startsWith('data:')) {
        setProfileImageUrl(url);
      } else {
        setProfileImageUrl(''); // Clear if it's a data URL or empty
      }
    }
  }, [creator]);

  if (!isOpen || !creator) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Only send fields that have changed
      const updates = {};
      if (formData.name !== (creator.name || '')) updates.name = formData.name;
      if (formData.handle !== (creator.handle || '')) updates.handle = formData.handle;
      if (formData.twitchUrl !== (creator.twitchUrl || '')) updates.twitchUrl = formData.twitchUrl || null;
      if (formData.kickUrl !== (creator.kickUrl || '')) updates.kickUrl = formData.kickUrl || null;
      if (formData.youtubeUrl !== (creator.youtubeUrl || '')) updates.youtubeUrl = formData.youtubeUrl || null;
      if (formData.tiktokUrl !== (creator.tiktokUrl || '')) updates.tiktokUrl = formData.tiktokUrl || null;
      if (formData.twitterUrl !== (creator.twitterUrl || '')) updates.twitterUrl = formData.twitterUrl || null;
      if (formData.instagramUrl !== (creator.instagramUrl || '')) updates.instagramUrl = formData.instagramUrl || null;
      if (formData.discordUrl !== (creator.discordUrl || '')) updates.discordUrl = formData.discordUrl || null;
      if (formData.contactEmail !== (creator.contactEmail || creator.email || '')) updates.contactEmail = formData.contactEmail || null;
      if (formData.country !== (creator.country || '')) updates.country = formData.country || null;
      if (formData.notes !== (creator.notes || '')) updates.notes = formData.notes || null;

      if (Object.keys(updates).length === 0) {
        setError('No changes to save');
        setLoading(false);
        return;
      }

      const response = await creatorsAPI.update(creator.id, updates);

      // Show success message with content preservation info
      const contentInfo = response.data.contentPreserved;
      let successMsg = 'Creator updated successfully!';
      if (response.data.platformIdsUpdated?.length > 0) {
        successMsg += ` Platform IDs updated: ${response.data.platformIdsUpdated.join(', ')}.`;
      }
      if (contentInfo) {
        successMsg += ` (${contentInfo.totalContent} content items preserved)`;
      }

      setSuccess(successMsg);

      // Wait a moment to show success message, then close
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update creator');
    } finally {
      setLoading(false);
    }
  };

  const handleFindEmail = async () => {
    setFindingEmail(true);
    setError('');
    setSuccess('');

    const force = Boolean(formData.contactEmail) && window.confirm(
      'This creator already has an email. Overwrite it with whatever we can find on their public bios?'
    );

    try {
      const response = await creatorsAPI.refreshEmail(creator.id, force);
      const { contactEmail, source, message } = response.data;
      if (contactEmail) {
        setFormData((prev) => ({ ...prev, contactEmail }));
        setSuccess(`Found email via ${source}: ${contactEmail}`);
      } else {
        setError(message || 'No public contact email found across any linked platform.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to find contact email');
    } finally {
      setFindingEmail(false);
    }
  };

  const handleRefreshPlatformIds = async () => {
    setRefreshingIds(true);
    setError('');
    setSuccess('');

    try {
      const response = await creatorsAPI.refreshPlatformIds(creator.id);
      const updates = response.data.updates || {};
      const updateCount = Object.keys(updates).length;

      if (updateCount > 0) {
        setSuccess(`Refreshed ${updateCount} platform ID(s): ${Object.keys(updates).join(', ')}`);
      } else {
        setSuccess('No platform IDs to refresh (no URLs configured)');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to refresh platform IDs');
    } finally {
      setRefreshingIds(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUpdatingImage(true);
    setError('');
    setSuccess('');

    try {
      // Read file as base64
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          // If it's a regular file, you could upload it to a CDN
          // For now, we'll use a public image URL approach
          // You can also convert to base64 and send directly
          console.log('Image selected:', file.name);
          
          // For direct URL input instead, user can paste URL in the text field below
          setSuccess('Please enter the image URL below (or paste public image URL)');
        } catch (err) {
          setError('Failed to process image');
        } finally {
          setUpdatingImage(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Failed to read image file');
      setUpdatingImage(false);
    }
  };

  const handleSaveProfileImage = async () => {
    if (!profileImageUrl.trim()) {
      setError('Please enter an image URL');
      return;
    }

    // Validate it's not a data URL
    if (profileImageUrl.startsWith('data:')) {
      setError('Please use a public image URL (not a local file). Paste the image URL from YouTube or a public image hosting service.');
      return;
    }

    setUpdatingImage(true);
    setError('');
    setSuccess('');

    try {
      // Call the PUT endpoint to update the profile image
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/creators/${creator.id}/profile-image`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ profileImageUrl: profileImageUrl })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update profile image');
      }

      const result = await response.json();
      setSuccess('Profile image updated successfully!');
      
      // Refresh the creator data after update
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to update profile image');
    } finally {
      setUpdatingImage(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Edit Creator</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
              {success}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
              />
            </div>

            {/* Handle */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Handle
              </label>
              <input
                type="text"
                name="handle"
                value={formData.handle}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
              />
            </div>

            {/* Profile Image URL */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profile Image URL
              </label>
              <div className="flex gap-2 items-start">
                <div className="flex-1">
                  <input
                    type="url"
                    value={profileImageUrl}
                    onChange={(e) => {
                      // Prevent data URLs
                      if (!e.target.value.startsWith('data:')) {
                        setProfileImageUrl(e.target.value);
                      }
                    }}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter a direct public image URL (e.g., from YouTube, imgur, or any image hosting service)
                  </p>
                </div>
              </div>
              {profileImageUrl && !profileImageUrl.startsWith('data:') && (
                <div className="mt-3">
                  <p className="text-xs text-gray-600 mb-2">Preview:</p>
                  <img 
                    src={profileImageUrl} 
                    alt="Profile preview" 
                    className="w-20 h-20 rounded-full object-cover border border-gray-300"
                    onError={() => setError('Image URL is broken or not accessible')}
                  />
                </div>
              )}
              <button
                type="button"
                onClick={handleSaveProfileImage}
                disabled={updatingImage || !profileImageUrl.trim() || profileImageUrl.startsWith('data:')}
                className="mt-3 px-4 py-2 bg-sc-pink text-white rounded hover:bg-sc-pink/90 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                {updatingImage ? 'Saving...' : 'Save Profile Image'}
              </button>
            </div>

            {/* Platform URLs Section */}
            <div className="md:col-span-2 mt-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Platform URLs</h3>
                <button
                  type="button"
                  onClick={handleRefreshPlatformIds}
                  disabled={refreshingIds}
                  className="text-sm px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                >
                  {refreshingIds ? 'Refreshing...' : 'Refresh Platform IDs'}
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                When you update a URL, the platform ID will be automatically refreshed.
              </p>
            </div>

            {/* Twitch URL */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Twitch URL
              </label>
              <input
                type="url"
                name="twitchUrl"
                value={formData.twitchUrl}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="https://twitch.tv/username"
              />
              {creator.twitchId && (
                <p className="text-xs text-gray-400 mt-1">Current Twitch ID: {creator.twitchId}</p>
              )}
            </div>

            {/* Kick URL */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kick URL
              </label>
              <input
                type="url"
                name="kickUrl"
                value={formData.kickUrl}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="https://kick.com/username"
              />
              {creator.kickId && (
                <p className="text-xs text-gray-400 mt-1">Current Kick ID: {creator.kickId}{creator.kickSlug ? ` (kick.com/${creator.kickSlug})` : ''}</p>
              )}
              {creator.kickUrl && !creator.kickId && (
                <p className="text-xs text-amber-500 mt-1">Kick channel not found for this URL yet. Check the spelling.</p>
              )}
            </div>

            {/* YouTube URL */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                YouTube URL
              </label>
              <input
                type="url"
                name="youtubeUrl"
                value={formData.youtubeUrl}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="https://youtube.com/channel/UC..."
              />
              {creator.channelId && (
                <p className="text-xs text-gray-400 mt-1">Current Channel ID: {creator.channelId}</p>
              )}
            </div>

            {/* TikTok URL */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                TikTok URL
              </label>
              <input
                type="url"
                name="tiktokUrl"
                value={formData.tiktokUrl}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="https://tiktok.com/@username"
              />
              {creator.tiktokUsername && (
                <p className="text-xs text-gray-400 mt-1">Current TikTok Username: {creator.tiktokUsername}</p>
              )}
            </div>

            {/* Twitter URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Twitter/X URL
              </label>
              <input
                type="url"
                name="twitterUrl"
                value={formData.twitterUrl}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="https://twitter.com/username"
              />
            </div>

            {/* Instagram URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instagram URL
              </label>
              <input
                type="url"
                name="instagramUrl"
                value={formData.instagramUrl}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="https://instagram.com/username"
              />
            </div>

            {/* Discord URL */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Discord URL
              </label>
              <input
                type="url"
                name="discordUrl"
                value={formData.discordUrl}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="https://discord.gg/invite"
              />
            </div>

            {/* Contact Info Section */}
            <div className="md:col-span-2 mt-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Info</h3>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Email
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                  placeholder="creator@example.com"
                />
                <button
                  type="button"
                  onClick={handleFindEmail}
                  disabled={findingEmail}
                  title="Scan public bios (Twitch, Kick, YouTube, Twitter, Instagram, TikTok) + any linked Linktree/carrd pages for a contact email"
                  className="whitespace-nowrap px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {findingEmail ? 'Searching…' : 'Find Email'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Scans bios across all linked platforms and follows Linktree/carrd-style pages. Can take up to a minute.
              </p>
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country
              </label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="USA"
              />
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="Any additional information..."
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-sc-pink text-white rounded-md hover:bg-sc-pink/90 disabled:bg-gray-400"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCreatorModal;
